<!-- src/components/admin/AdminUserEditor.vue -->
<template>
  <v-card class="rounded-xl">
    <!-- Header -->
    <v-sheet color="primary" dark class="pa-4 d-flex align-center">
      <v-icon large class="mr-3">mdi-account-edit</v-icon>
      <div class="flex-grow-1">
        <div class="text-h6 font-weight-bold">
          {{ model.id ? $t('Modifier utilisateur') : $t('Créer utilisateur') }}
        </div>
        <div class="text-caption" v-if="model.id">
          ID: {{ model.id }} • {{ model.email || model.username }}
        </div>
      </div>
      <v-btn icon @click="$emit('cancel')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-sheet>
    
    <v-card-text class="pt-6">
      <v-form ref="form" @submit.prevent="save">
        <!-- Section: Informations générales -->
        <div class="text-subtitle-1 font-weight-bold primary--text mb-3">
          <v-icon small class="mr-2" color="primary">mdi-account-circle</v-icon>
          {{ $t('Informations personnelles') }}
        </div>
        
        <v-row>
          <!-- Prénom -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="model.first_name"
              :label="$t('Prénom')"
              prepend-inner-icon="mdi-account"
              outlined
              dense
              hint="Ex: Jean"
            />
          </v-col>
          
          <!-- Nom de famille -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="model.last_name"
              :label="$t('Nom de famille')"
              prepend-inner-icon="mdi-account"
              outlined
              dense
              hint="Ex: Dupont"
            />
          </v-col>
          
          <!-- Nom complet -->
          <v-col cols="12">
            <v-text-field
              v-model="model.fullname"
              :label="$t('Nom complet') + ' *'"
              prepend-inner-icon="mdi-card-account-details"
              outlined
              dense
              :rules="[v => !!v || $t('Champ requis')]"
              hint="Ex: Jean Dupont (ou nom complet si différent)"
            />
          </v-col>
        </v-row>
        
        <v-divider class="my-4" />
        
        <!-- Section: Identifiants -->
        <div class="text-subtitle-1 font-weight-bold primary--text mb-3">
          <v-icon small class="mr-2" color="primary">mdi-shield-account</v-icon>
          {{ $t('Identifiants de connexion') }}
        </div>
        
        <v-row>
          <!-- Nom d'utilisateur -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="model.username"
              :label="$t('Nom d\'utilisateur') + ' *'"
              prepend-inner-icon="mdi-at"
              outlined
              dense
              :disabled="!!model.id"
              :hint="model.id ? $t('Non modifiable après création') : ''"
              persistent-hint
              :rules="[v => !!v || $t('Champ requis')]"
            />
          </v-col>
          
          <!-- Email -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="model.email"
              :label="$t('Email')"
              prepend-inner-icon="mdi-email"
              type="email"
              outlined
              dense
              :rules="emailRules"
              hint="Ex: jean.dupont@example.com"
            />
          </v-col>
        </v-row>
        
        <v-divider class="my-4" />
        
        <!-- Section: Contact -->
        <div class="text-subtitle-1 font-weight-bold primary--text mb-3">
          <v-icon small class="mr-2" color="primary">mdi-card-account-phone</v-icon>
          {{ $t('Informations de contact') }}
        </div>
        
        <v-row>
          <!-- Téléphone -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="model.phone_number"
              :label="$t('Téléphone')"
              prepend-inner-icon="mdi-phone"
              outlined
              dense
              placeholder="+2250700000000"
              :rules="phoneRules"
              hint="Format international avec +"
            />
          </v-col>
          
          <!-- Date de naissance -->
          <v-col cols="12" md="6">
            <v-menu
              v-model="birthdateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="model.birthdate"
                  :label="$t('Date de naissance')"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                  clearable
                  hint="Sélectionnez la date"
                />
              </template>
              <v-date-picker
                v-model="model.birthdate"
                @input="birthdateMenu = false"
                :max="maxBirthdate"
                locale="fr"
                scrollable
              />
            </v-menu>
          </v-col>
        </v-row>
        
        <v-divider class="my-4" />
        
        <!-- Section: Type d'utilisateur -->
        <div class="text-subtitle-1 font-weight-bold primary--text mb-3">
          <v-icon small class="mr-2" color="primary">mdi-account-group</v-icon>
          {{ $t('Type d\'utilisateur') }}
        </div>
        
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-checkbox
              v-model="model.is_mobile"
              hide-details
              color="blue"
              class="mt-0"
            >
              <template v-slot:label>
                <div class="d-flex align-center">
                  <v-icon small class="mr-2" color="blue">mdi-cellphone</v-icon>
                  <span>{{ $t('Mobile') }}</span>
                </div>
              </template>
            </v-checkbox>
          </v-col>
          
          <v-col cols="12" sm="6" md="3">
            <v-checkbox
              v-model="model.is_web"
              hide-details
              color="green"
              class="mt-0"
            >
              <template v-slot:label>
                <div class="d-flex align-center">
                  <v-icon small class="mr-2" color="green">mdi-monitor</v-icon>
                  <span>{{ $t('Web') }}</span>
                </div>
              </template>
            </v-checkbox>
          </v-col>
          
          <v-col cols="12" sm="6" md="3">
            <v-checkbox
              v-model="model.is_pro_mobile"
              hide-details
              color="purple"
              class="mt-0"
            >
              <template v-slot:label>
                <div class="d-flex align-center">
                  <v-icon small class="mr-2" color="purple">mdi-briefcase</v-icon>
                  <span>{{ $t('Pro Mobile') }}</span>
                </div>
              </template>
            </v-checkbox>
          </v-col>
          
          <v-col cols="12" sm="6" md="3">
            <v-checkbox
              v-model="model.is_vehicle"
              hide-details
              color="orange"
              class="mt-0"
            >
              <template v-slot:label>
                <div class="d-flex align-center">
                  <v-icon small class="mr-2" color="orange">mdi-car</v-icon>
                  <span>{{ $t('Véhicule') }}</span>
                </div>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>
        
        <!-- Alert si aucun type -->
        <v-alert 
          v-if="!hasAnyType" 
          type="info" 
          color="secondary"
          text 
          dense 
          class="mt-4"
          border="left"
        >
          <div class="d-flex align-center">
            <div>
              <div class="font-weight-medium">{{ $t('Utilisateur sans smartphone') }}</div>
              <div class="text-caption">
                {{ $t('Cet utilisateur sera classé comme "Sans smartphone" (enregistré par pompiste)') }}
              </div>
            </div>
          </div>
        </v-alert>
        
        <v-divider class="my-4" />
        
        <!-- Section: Statut du compte -->
        <div class="text-subtitle-1 font-weight-bold primary--text mb-3">
          <v-icon small class="mr-2" color="primary">mdi-shield-check</v-icon>
          {{ $t('Statut du compte') }}
        </div>
        
        <v-row>
          <v-col cols="12">
            <v-switch
              v-model="model.is_active"
              inset
              :color="model.is_active ? 'success' : 'error'"
              class="mt-0"
            >
              <template v-slot:prepend>
                <v-icon :color="model.is_active ? 'success' : 'error'" class="mr-2">
                  {{ model.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                </v-icon>
              </template>
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">
                    {{ model.is_active ? $t('Compte actif') : $t('Compte bloqué') }}
                  </div>
                  <div class="text-caption grey--text">
                    {{ model.is_active ? $t('L\'utilisateur peut se connecter') : $t('L\'utilisateur ne peut pas se connecter') }}
                  </div>
                </div>
              </template>
            </v-switch>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    
    <v-divider />
    
    <!-- Actions -->
    <v-card-actions class="pa-4 justify-end">
      <v-btn 
        text 
        @click="$emit('cancel')" 
        :disabled="saving"
        large
      >
        <v-icon left>mdi-close</v-icon>
        {{ $t('Annuler') }}
      </v-btn>
      <v-btn 
        color="primary" 
        @click="save" 
        :loading="saving"
        :disabled="!isValid"
        large
        elevation="2"
      >
        <v-icon left>mdi-content-save</v-icon>
        {{ $t('Enregistrer') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import AdminServices from "@/services/AdminServices"

export default {
  name: "AdminUserEditor",
  props: { 
    value: {
      type: Object,
      required: true
    }
  },
  
  data() {
    return { 
      model: { ...this.value },
      saving: false,
      birthdateMenu: false,
      
      emailRules: [
        v => !v || /.+@.+\..+/.test(v) || this.$t('Email invalide'),
       // v => !v || !v.toLowerCase().includes('@gwassou') || this.$t('Emails @gwassou non autorisés'),
      ],
      
      phoneRules: [
        v => !v || /^\+?1?\d{9,15}$/.test(v) || this.$t('Format téléphone invalide (ex: +2250700000000)'),
      ],
    }
  },
  
  computed: {
    maxBirthdate() {
      // Max = aujourd'hui - 13 ans (âge minimum)
      const d = new Date();
      d.setFullYear(d.getFullYear() - 13);
      return d.toISOString().split('T')[0];
    },
    
    hasAnyType() {
      return this.model.is_mobile || 
             this.model.is_web || 
             this.model.is_pro_mobile || 
             this.model.is_vehicle;
    },
    
    isValid() {
      return !!(
        this.model.fullname && 
        this.model.fullname.trim() &&
        this.model.username &&
        this.model.username.trim()
      );
    },
  },
  
  watch: {
    value: {
      handler(v) { 
        this.model = { ...v };
      },
      deep: true
    },
  },
  
  methods: {
    async save() {
      // Validation du formulaire
      if (!this.$refs.form.validate()) {
        this.$store.commit("app/message", { 
          msg: this.$t('Veuillez corriger les erreurs dans le formulaire'), 
          color: "warning" 
        });
        return;
      }
      
      if (!this.isValid) {
        this.$store.commit("app/message", { 
          msg: this.$t('Le nom complet et le nom d\'utilisateur sont obligatoires'), 
          color: "warning" 
        });
        return;
      }
      
      this.saving = true;
      
      try {
        const payload = {
          first_name: this.model.first_name?.trim() || "",
          last_name: this.model.last_name?.trim() || "",
          fullname: this.model.fullname?.trim(),
          username: this.model.username?.trim(),
          email: this.model.email?.trim() || null,
          phone_number: this.model.phone_number?.trim() || null,
          birthdate: this.model.birthdate || null,
          is_web: !!this.model.is_web,
          is_mobile: !!this.model.is_mobile,
          is_pro_mobile: !!this.model.is_pro_mobile,
          is_vehicle: !!this.model.is_vehicle,
          is_active: !!this.model.is_active,
        };
        
        if (this.model.id) {
          // Modification
          await AdminServices.updateUser(this, this.model.id, payload);
          this.$emit("saved");
        } else {
          // Création (si implémentée côté backend)
          await AdminServices.createUser(this, payload);
          this.$emit("saved");
        }
      } catch (e) {
        let msg = this.$t("messages.error.operation_failed");
        
        // Gestion des erreurs spécifiques du backend
        if (e?.response?.data) {
          const data = e.response.data;
          
          if (data.email) {
            msg = `Email: ${Array.isArray(data.email) ? data.email[0] : data.email}`;
          } else if (data.phone_number) {
            msg = `Téléphone: ${Array.isArray(data.phone_number) ? data.phone_number[0] : data.phone_number}`;
          } else if (data.username) {
            msg = `Nom d'utilisateur: ${Array.isArray(data.username) ? data.username[0] : data.username}`;
          } else if (data.first_name) {
            msg = `Prénom: ${Array.isArray(data.first_name) ? data.first_name[0] : data.first_name}`;
          } else if (data.last_name) {
            msg = `Nom: ${Array.isArray(data.last_name) ? data.last_name[0] : data.last_name}`;
          } else if (data.detail) {
            msg = data.detail;
          } else if (data.non_field_errors) {
            msg = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors;
          }
        }
        
        this.$store.commit("app/message", { msg, color: "error" });
      } finally {
        this.saving = false;
      }
    },
  },
}
</script>

<style scoped>
/* Amélioration visuelle des sections */
.text-subtitle-1 {
  border-left: 4px solid;
  padding-left: 8px;
}

/* Animation du bouton au survol */
.v-btn:not(.v-btn--disabled):hover {
  transform: translateY(-2px);
  transition: transform 0.2s;
}

/* Amélioration des inputs */
.v-text-field >>> .v-input__prepend-inner {
  margin-top: 8px !important;
}

/* Amélioration du switch */
.v-input--switch >>> .v-input__slot {
  margin-bottom: 0 !important;
}
</style>